import {AceLoggerService} from '@logging/ace-logger.service';
import {Component, OnInit, forwardRef, EventEmitter, ChangeDetectorRef, OnDestroy, SimpleChanges} from '@angular/core';
import {Input, Output, OnChanges} from '@angular/core';
import {ValidatorFn, AbstractControl, ControlValueAccessor, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validators, ValidationErrors} from '@angular/forms';
import {NgbTimepickerConfig, NgbTimeStruct} from '@ng-bootstrap/ng-bootstrap';
import {AceLogger} from '@logging/ace-logger';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {ElementIdGenerationService} from '@app/shared/services/util/html-elements/element-id-generation.service';
import {DAY_MINUTES, HOUR_MINUTES} from '@app/shared/util/general/duration-utils';

export interface PickerSchedule {
  enabled: boolean;
  start: NgbTimeStruct;
  end: NgbTimeStruct;
}

@Component({
  selector: 'app-time-picker-day',
  templateUrl: './time-picker-day.component.html',
  styleUrls: ['./time-picker-day.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => TimePickerDayComponent)
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => TimePickerDayComponent)
    }
  ],
  standalone: false
})


export class TimePickerDayComponent implements OnInit, OnDestroy, ControlValueAccessor, OnChanges {

  readonly resetModel: PickerSchedule =
    {enabled: false, start: {hour: 0, minute: 0, second: 0}, end: {hour: 0, minute: 0, second: 0}};

  @Input() schedule: PickerSchedule;
  @Input() index: number;
  @Input() dayOfWeek: string;
  @Input() day: UntypedFormGroup;
  @Input() readonly = false;
  @Output() closeEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() formReady: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();
  @Output() copiedSchedule: EventEmitter<any> = new EventEmitter<any>();

  private readonly logger: AceLogger;
  private readonly ngUnsubscribe: Subject<void> = new Subject();

  onChange: (any) => void = (_) => {};
  onTouched: () => void = () => {};
  readonly onRadioId: string;
  readonly offRadioId: string;

  model: PickerSchedule;
  enabled: boolean;

  constructor(config: NgbTimepickerConfig, private fb: UntypedFormBuilder, private cdRef: ChangeDetectorRef, private logService: AceLoggerService,
              elementIdGenerationService: ElementIdGenerationService) {
    this.logger = this.logService.create(TimePickerDayComponent.name);
    config.seconds = false;
    config.spinners = false;
    const generator = elementIdGenerationService.buildIdGenerator();
    this.onRadioId = generator.getId('on');
    this.offRadioId = generator.getId('off');
    this.day = this.fb.group({
      enabled: new UntypedFormControl(null),
      start: new UntypedFormControl(null, [Validators.required]),
      end: new UntypedFormControl(null, [Validators.required])
    }, {validators: [this.validateStartBeforeEnd]});
  }

  ngOnInit() {
    this.enabled = this.schedule.enabled;
    this.model = this.schedule;
    this.day.get('start').valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event$) => this.onStartTimeChange(event$));
    this.day.get('end').valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event$) => this.onEndTimeChange(event$));
    this.day.get('enabled').valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event$) => this.onEnabledChange(event$));
    this.formReady.emit(this.day);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.schedule) {
      this.day.get('start').setValue(this.schedule.start);
      this.day.get('end').setValue(this.schedule.end);
      this.day.get('enabled').setValue(this.schedule.enabled);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  copyTime(validDay: boolean, singleDay: boolean, direction: 'right'|'left', index: number) {
    if (validDay) {
      this.logger.debug('copyTime', this.day.value, validDay, singleDay, direction, index);
      this.copiedSchedule.emit({
          schedule: this.day.value,
          singleDay: singleDay,
          direction: direction,
          index: index
        });
    } else {
      this.logger.debug('copyTime: Invalid day schedule', this.day.value, );
    }
  }

  onEnabledChange(event: boolean) {
    this.enabled = event;
    this.logger.debug('onEnabledChange', this.enabled);
  }

  onStartTimeChange(event: NgbTimeStruct) {
    this.onChange(event);
    this.model.start = event;
  }

  onEndTimeChange(event: NgbTimeStruct) {
    this.onChange(event);
    this.model.end = event;
  }

  writeValue(obj: any): void {
    this.model = obj != null ? obj : this.resetModel;
    this.enabled = this.model.enabled;
    this.day.get('start').setValue(this.model.start, {emitEvent: false});
    this.day.get('end').setValue(this.model.end, {emitEvent: false});
    this.day.get('enabled').setValue(this.enabled, {emitEvent: false});
    this.cdRef.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validateStartBeforeEnd: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const resetTime: NgbTimeStruct = {hour: 0, minute: 0, second: 0};
    const enabled: boolean = control.get('enabled').value;
    let start: NgbTimeStruct = control.get('start').value;
    let end: NgbTimeStruct = control.get('end').value;

    if (!enabled) {
      if (start === null) {
        this.logger.debug('resetting start');
        control.get('start').setValue(resetTime);
        start = resetTime;
      }
      if (end === null) {
        this.logger.debug('resetting end');
        control.get('end').setValue(resetTime);
        end = resetTime;
      }
      return null;
    } else if (start === null || end === null) {
      this.logger.debug('ValidatePickers null', start, end);
      return {invalidTime: true};
    } else {
      const startMinutes = (start.hour * HOUR_MINUTES) + start.minute;
      // An end of 00:00 means the end of the day, so it counts as 1440, not 0.
      const endMinutes = ((end.hour * HOUR_MINUTES) + end.minute) || DAY_MINUTES;
      if (startMinutes >= endMinutes || start.hour > 23 || start.minute > 59 || end.hour > 23 || end.minute > 59) {
        this.logger.debug('ValidateStartBeforeEnd invalid time', startMinutes, '>=', endMinutes);
        return {invalidTime: true};
      }
      return null;
    }
  }

}
